import { useState, useEffect } from "react";
import { useAuth, useUser, UserButton } from "@clerk/clerk-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CalendarDays, Trophy, TrendingUp, Clock, Loader2 } from "lucide-react";
import { client } from "@/client";

interface Stats {
  totalCheckIns: number;
  monthlyCheckIns: number;
  currentStreak: number;
  lastCheckIn: string | null;
}

interface PointsData {
  totalPoints: number;
  transactions: Array<{
    id: number;
    amount: number;
    type: string;
    description: string;
    createdAt: string;
  }>;
}

interface LeaderboardEntry {
  rank: number;
  userId: number;
  name: string;
  imageUrl: string;
  totalPoints: number;
}

interface MyRank {
  rank: number;
  totalPoints: number;
}

export function MemberDashboard() {
  const { getToken } = useAuth();
  const { user } = useUser();
  const [stats, setStats] = useState<Stats | null>(null);
  const [pointsData, setPointsData] = useState<PointsData | null>(null);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [myRank, setMyRank] = useState<MyRank | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      try {
        const token = await getToken();
        const headers = { Authorization: `Bearer ${token}` };

        const [statsRes, pointsRes, leaderboardRes] = await Promise.all([
          client.api["check-ins"].stats.$get({}, { headers }),
          client.api.points.my.$get({}, { headers }),
          client.api.points.leaderboard.$get({ query: { limit: "5" } }, { headers }),
        ]);

        const [statsData, pointsResData, leaderboardData] = await Promise.all([
          statsRes.json() as Promise<Stats>,
          pointsRes.json() as Promise<PointsData>,
          leaderboardRes.json() as Promise<{ leaderboard: LeaderboardEntry[]; myRank: MyRank | null }>,
        ]);

        setStats(statsData);
        setPointsData(pointsResData);
        setLeaderboard(leaderboardData.leaderboard);
        setMyRank(leaderboardData.myRank);
      } catch (err) {
        console.error("Failed to fetch dashboard data:", err);
      } finally {
        setIsLoading(false);
      }
    }

    fetchData();
  }, [getToken]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b">
        <div className="container flex h-16 items-center justify-between px-4">
          <h1 className="text-xl font-bold">목요모임</h1>
          <UserButton />
        </div>
      </header>

      <main className="container px-4 py-6 space-y-6">
        {/* Welcome */}
        <div>
          <h2 className="text-2xl font-bold">
            안녕하세요, {user?.firstName ?? "회원"}님!
          </h2>
          <p className="text-muted-foreground">오늘도 좋은 하루 되세요.</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-1">
                <Trophy className="h-4 w-4" />
                내 포인트
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-primary">
                {pointsData?.totalPoints ?? 0}
              </p>
              {myRank && (
                <p className="text-xs text-muted-foreground">
                  전체 {myRank.rank}위
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-1">
                <CalendarDays className="h-4 w-4" />
                총 출석
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{stats?.totalCheckIns ?? 0}회</p>
              <p className="text-xs text-muted-foreground">
                이번 달 {stats?.monthlyCheckIns ?? 0}회
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-1">
                <TrendingUp className="h-4 w-4" />
                연속 출석
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{stats?.currentStreak ?? 0}주</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                마지막 출석
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm font-medium">
                {stats?.lastCheckIn
                  ? formatDate(stats.lastCheckIn)
                  : "기록 없음"}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Leaderboard */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">리더보드</CardTitle>
            <CardDescription>이번 달 상위 출석자</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {leaderboard.map((entry) => (
                <div
                  key={entry.userId}
                  className="flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <span
                      className={`w-6 text-center font-bold ${
                        entry.rank === 1
                          ? "text-yellow-500"
                          : entry.rank === 2
                          ? "text-gray-400"
                          : entry.rank === 3
                          ? "text-orange-400"
                          : "text-muted-foreground"
                      }`}
                    >
                      {entry.rank}
                    </span>
                    <img
                      src={entry.imageUrl || "/placeholder-avatar.png"}
                      alt={entry.name ?? ""}
                      className="h-8 w-8 rounded-full"
                    />
                    <span className="font-medium">{entry.name}</span>
                  </div>
                  <span className="text-primary font-semibold">
                    {entry.totalPoints}점
                  </span>
                </div>
              ))}
            </div>
            <Button variant="outline" className="w-full mt-4">
              전체 순위 보기
            </Button>
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">최근 활동</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {pointsData?.transactions.slice(0, 5).map((tx) => (
                <div
                  key={tx.id}
                  className="flex items-center justify-between text-sm"
                >
                  <div>
                    <p className="font-medium">{tx.description}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatDate(tx.createdAt)}
                    </p>
                  </div>
                  <span
                    className={`font-semibold ${
                      tx.amount > 0 ? "text-green-600" : "text-red-600"
                    }`}
                  >
                    {tx.amount > 0 ? "+" : ""}
                    {tx.amount}
                  </span>
                </div>
              ))}
              {(!pointsData?.transactions || pointsData.transactions.length === 0) && (
                <p className="text-muted-foreground text-center py-4">
                  아직 활동 내역이 없습니다
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

function formatDate(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleDateString("ko-KR", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
